import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuLogOut } from "react-icons/lu";
import { useAuth } from "../lib/auth";
import { isValidPhone, updateProfile } from "../lib/profiles";
import { Spinner } from "../components/ui/Loader";
import { TelegramConnectCard } from "../components/shared/TelegramConnectCard";

export const Profile = () => {
  const { profile, setProfile, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.name ?? "");
      setCompany(profile.company ?? "");
      setPhone(profile.phone ?? "");
      setCity(profile.city ?? "");
    }
  }, [profile]);

  const handleSave = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (!name.trim()) {
      setError("Вкажіть імʼя");
      return;
    }
    if (!isValidPhone(phone)) {
      setError("Вкажіть коректний номер телефону");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateProfile({
        name: name.trim(),
        company: company.trim() || null,
        phone: phone.trim(),
        city: city.trim() || null,
      });
      setProfile(updated);
      setSaved(true);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <main className="flex-1 px-5 py-8 md:px-8">
      <h1 className="text-2xl font-bold text-white">Профіль</h1>

      <div className="mt-5 md:hidden">
        <TelegramConnectCard />
      </div>

      <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="card p-4 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-bold text-white">Особисті дані</h2>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-sm font-medium text-brand transition hover:opacity-80"
              >
                Редагувати
              </button>
            )}
          </div>
          <form onSubmit={handleSave} className="mt-3 sm:mt-5 flex flex-col gap-2 sm:gap-4">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-neutral-300">
                Повне імʼя
              </span>
              {editing ? (
                <input
                  className="input-dark"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                />
              ) : (
                <span className="block text-sm text-white">{profile?.name ?? "—"}</span>
              )}
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-neutral-300">
                Компанія
              </span>
              {editing ? (
                <input
                  className="input-dark"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  autoComplete="organization"
                />
              ) : (
                <span className="block text-sm text-white">{profile?.company ?? "—"}</span>
              )}
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-neutral-300">
                Телефон
              </span>
              {editing ? (
                <input
                  className="input-dark"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  required
                />
              ) : (
                <span className="block text-sm text-white">{profile?.phone ?? "—"}</span>
              )}
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-neutral-300">
                Місто
              </span>
              {editing ? (
                <input
                  className="input-dark"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              ) : (
                <span className="block text-sm text-white">{profile?.city ?? "—"}</span>
              )}
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-neutral-300">
                Email
              </span>
              {editing ? (
                <input
                  className="input-dark opacity-70"
                  value={profile?.email ?? ""}
                  readOnly
                  aria-readonly
                />
              ) : (
                <span className="block text-sm text-white">{profile?.email ?? "—"}</span>
              )}
            </label>
            {editing && error && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}
            {!editing && saved && (
              <p className="rounded border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
                Збережено
              </p>
            )}
            {editing && (
              <button
                type="submit"
                disabled={saving}
                className="mt-3 btn-brand w-full justify-center disabled:opacity-70"
              >
                {saving ? (
                  <>
                    <Spinner className="h-4 w-4" />
                    Зачекайте…
                  </>
                ) : (
                  "Зберегти"
                )}
              </button>
            )}
          </form>
        </section>
      </div>

      <div className="sm:hidden mt-10 flex items-center justify-between gap-3 rounded-lg bg-white/5 px-4 py-3">
        <div className="min-w-0 text-sm">
          <p className="truncate font-semibold text-white">{profile?.name}</p>
          <p className="truncate text-xs text-neutral-400">{user?.email}</p>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          className="flex items-center gap-1.5 text-sm font-medium text-neutral-300 transition hover:text-red-500"
        >
          Вийти
          <LuLogOut aria-hidden />
        </button>
      </div>
    </main>
  );
};
